import React, { useCallback, useContext, useEffect, useState } from "react"
import ContactBoatsTable from "src/main/Contact/ContactBoatsTable"
import { ContactsContext } from "src/main/Contact/index"

import BasePopover from "src/components/BasePopover"
import BaseSlider from "src/components/BaseSlider"
import Chips from "src/components/Chips"
import Form from "src/components/Form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "src/components/Select"
import Tooltip from "src/components/Tooltip"

import {
  getContactBoats,
  getContactBoatsSelectableIds,
  printContactBoats,
} from "src/api/ContactBoat"

import useDatatable from "src/hooks/use_datatable"
import useDebounce from "src/hooks/use_debounce"
import useWindowSize from "src/hooks/use_window_size"

import { capitalize, titlecase } from "src/utils/string_helpers"

const ContactBoatsTab = () => {
  const { longestLoa } = useContext(ContactsContext)
  const MAX_LOA = longestLoa
  const MIN_LOA = 0

  const table = useDatatable({
    queryKey: "contactBoats",
    fetchFn: getContactBoats,
    defaultFilters: {
      searchTerm: "",
      status: "active",
      boatType: [],
      insurance: [],
      registration: [],
      loaMin: MIN_LOA,
      loaMax: MAX_LOA,
    },
    fetchAllIds: (filterParams) =>
      getContactBoatsSelectableIds(filterParams).then((r) => r.ids),
    idKey: "encodedId",
  })

  const [searchInputValue, setSearchInputValue] = useState(
    table.filters.searchTerm || ""
  )
  const [loaRange, setLoaRange] = useState([
    table.filters.loaMin ?? MIN_LOA,
    table.filters.loaMax ?? MAX_LOA,
  ])
  const [debouncedSetSearch] = useDebounce((value) => {
    table.setFilter("searchTerm", value)
  })

  const { isXLScreen } = useWindowSize()

  useEffect(() => {
    if (!table.filters.searchTerm) setSearchInputValue("")
  }, [table.filters.searchTerm])

  useEffect(() => {
    if (table.filters.loaMin === MIN_LOA && table.filters.loaMax === MAX_LOA) {
      setLoaRange([MIN_LOA, MAX_LOA])
    }
  }, [table.filters.loaMin, table.filters.loaMax, MAX_LOA])

  const handleSearch = useCallback(
    (e) => {
      const value = e.target.value
      setSearchInputValue(value)
      debouncedSetSearch(value.trim())
    },
    [debouncedSetSearch]
  )

  const handleClearSearch = useCallback(() => {
    setSearchInputValue("")
    table.clearFilter("searchTerm")
  }, [table])

  const handleLoaRangeChange = useCallback(
    (values) => {
      setLoaRange(values)
      table.setFilters({ loaMin: values[0], loaMax: values[1] })
    },
    [table]
  )

  const handleReset = useCallback(() => {
    table.clearAllFilters()
    setSearchInputValue("")
  }, [table])

  const renderContactBoatSearch = () => (
    <Form.IconTextField
      icon={
        <i className="fa-solid fa-magnifying-glass text-xl text-gray-400" />
      }
      id="contact-boat-search"
      isClearable
      onChange={handleSearch}
      onClearSelection={handleClearSearch}
      position="left"
      value={searchInputValue}
      placeholder="Search boat name, captain, registration or HIN"
    />
  )

  const renderBoatTypeFilter = () => (
    <Select
      multiple
      value={table.filters.boatType}
      onValueChange={(values) => table.setFilter("boatType", values)}
    >
      <SelectTrigger variant="button" className="w-full" aria-label="Type">
        Type
      </SelectTrigger>
      <SelectContent className="max-h-none">
        <SelectItem value="power">Power</SelectItem>
        <SelectItem value="sail">Sail</SelectItem>
      </SelectContent>
    </Select>
  )

  const renderStatusFilter = (field) => (
    <Select
      multiple
      value={table.filters[field]}
      onValueChange={(values) => table.setFilter(field, values)}
    >
      <SelectTrigger
        variant="button"
        className="w-full"
        aria-label={capitalize(field)}
      >
        {capitalize(field)}
      </SelectTrigger>
      <SelectContent className="max-h-none">
        {["active", "missing", "expired"].map((value) => (
          <SelectItem key={value} value={value}>
            {capitalize(value)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  const renderLoaFilter = () => (
    <BasePopover label="LOA" width={isXLScreen ? "400px" : "300px"}>
      <div className="grid grid-cols-12 gap-2 space-y-2 px-6 pb-6 pt-8">
        <div className="col-span-12 flex justify-between">
          <span>{MIN_LOA}&apos;</span>
          <span>{MAX_LOA}&apos;</span>
        </div>
        <div className="col-span-12">
          <BaseSlider
            value={loaRange}
            onValueChange={handleLoaRangeChange}
            min={MIN_LOA}
            max={MAX_LOA}
          />
        </div>
        <div className="col-span-6 flex flex-col">
          <Form.Label htmlFor="loaMin">Minimum</Form.Label>
          <Form.IconTextField
            id="loaMin"
            icon="ft"
            position="right"
            type="number"
            value={table.filters.loaMin}
            onChange={(e) => {
              const val = parseInt(e.target.value) || 0
              table.setFilter("loaMin", val)
            }}
          />
        </div>
        <div className="col-span-6 flex flex-col">
          <div className="flex">
            <Form.Label htmlFor="loaMax">Maximum</Form.Label>
            <div className="ml-2">
              <Tooltip
                text="Dynamically changes based on largest boat size"
                placement="top"
                variant="dark"
              >
                <i className="fa-solid fa-circle-info" />
              </Tooltip>
            </div>
          </div>
          <Form.IconTextField
            id="loaMax"
            icon="ft"
            position="right"
            type="number"
            value={table.filters.loaMax}
            onChange={(e) => {
              const val = parseInt(e.target.value) || 0
              table.setFilter("loaMax", val)
            }}
          />
        </div>
      </div>
    </BasePopover>
  )

  const renderArchiveStatusFilter = () => (
    <Select
      value={table.filters.status}
      onValueChange={(value) => table.setFilter("status", value || "active")}
    >
      <SelectTrigger variant="button" className="w-full" aria-label="Status">
        {capitalize(table.filters.status)}
      </SelectTrigger>
      <SelectContent className="max-h-none">
        <SelectItem value="active">Active</SelectItem>
        <SelectItem value="archived">Archived</SelectItem>
      </SelectContent>
    </Select>
  )

  const renderFilters = () => (
    <div className="flex w-full flex-wrap space-y-2 sm:w-min sm:flex-nowrap sm:space-x-2 sm:space-y-0">
      {renderArchiveStatusFilter()}
      {renderBoatTypeFilter()}
      {renderStatusFilter("insurance")}
      {renderStatusFilter("registration")}
      {renderLoaFilter()}
    </div>
  )

  const buildChips = () => {
    const chips = []

    if (table.filters.searchTerm) {
      chips.push({
        key: "searchTerm",
        text: `Search: ${table.filters.searchTerm}`,
        onClick: handleClearSearch,
      })
    }

    table.filters.boatType.forEach((type) => {
      chips.push({
        key: `type-${type}`,
        text: `Type: ${titlecase(type)}`,
        onClick: () =>
          table.setFilter(
            "boatType",
            table.filters.boatType.filter((t) => t !== type)
          ),
      })
    })

    table.filters.insurance.forEach((filter) => {
      chips.push({
        key: `insurance-${filter}`,
        text: `Insurance: ${capitalize(filter)}`,
        onClick: () =>
          table.setFilter(
            "insurance",
            table.filters.insurance.filter((f) => f !== filter)
          ),
      })
    })

    table.filters.registration.forEach((filter) => {
      chips.push({
        key: `registration-${filter}`,
        text: `Registration: ${capitalize(filter)}`,
        onClick: () =>
          table.setFilter(
            "registration",
            table.filters.registration.filter((f) => f !== filter)
          ),
      })
    })

    if (table.filters.loaMin !== MIN_LOA || table.filters.loaMax !== MAX_LOA) {
      chips.push({
        key: "loa",
        text: `LOA: ${table.filters.loaMin} - ${table.filters.loaMax} ft`,
        onClick: () => {
          table.setFilters({ loaMin: MIN_LOA, loaMax: MAX_LOA })
        },
      })
    }

    return chips
  }

  const chips = buildChips()

  return (
    <div>
      <div className="mt-2 flex flex-col px-4 pb-6 lg:p-0 xl:flex-row print:hidden">
        <div className="w-full pb-2 xl:mr-2 xl:w-[336px] xl:p-0">
          {renderContactBoatSearch()}
        </div>
        {renderFilters()}
      </div>
      {chips.length > 0 && (
        <div className="px-4 xl:px-0 print:hidden">
          <Chips onReset={handleReset}>
            {chips.map((chip) => (
              <Chips.Chip
                key={chip.key}
                text={chip.text}
                onClick={chip.onClick}
              />
            ))}
          </Chips>
        </div>
      )}
      <div className="mt-4 overflow-auto pb-12 xl:overflow-visible">
        <ContactBoatsTable
          table={table}
          status={table.filters.status}
          onPrint={(ids) => {
            const { page, ...printParams } = table.fetchParams
            return printContactBoats({ ids, ...printParams })
          }}
        />
      </div>
    </div>
  )
}

export default ContactBoatsTab
